import type { QueryClient } from "@tanstack/react-query";
import { Outlet, createRootRouteWithContext, lazyRouteComponent } from "@tanstack/react-router";
import { Suspense } from "react";
import { NotFound } from "@/components/feedback/not-found";
import { RouteError } from "@/components/feedback/route-error";
import { usePrimaryActionHotkey } from "@/lib/primary-action";
import { useHotkeySheetOpen, useScreenHotkeyDispatcher } from "@/lib/screen-hotkeys";

export interface RouterContext {
  queryClient: QueryClient;
}

// Devtools only in development; the production bundle never imports them.
const Devtools = import.meta.env.DEV
  ? lazyRouteComponent(async () => {
      const [router, query] = await Promise.all([
        import("@tanstack/react-router-devtools"),
        import("@tanstack/react-query-devtools"),
      ]);
      return {
        default: () => (
          <>
            <router.TanStackRouterDevtools position="bottom-right" />
            <query.ReactQueryDevtools buttonPosition="bottom-left" />
          </>
        ),
      };
    })
  : () => null;

/**
 * THE DISPATCHER IS EAGER AND THE SHEET IS NOT.
 *
 * The listener has to be installed before the first keystroke, and it is small:
 * a registry and a keydown handler. The `?` sheet is a DIALOGUE — the modal
 * surface, the focus trap, the portal — and nobody sees it until they ask for
 * it, so fetching it on the first paint is 25 kB gzipped every terminal waits
 * for to answer a question most shifts never ask. The rule the POC states about
 * a contribution barrel, arriving at the keyboard layer: the thing that
 * REGISTERS must not name the thing that RENDERS.
 */
const LazyHotkeySheet = lazyRouteComponent(() => import("@/components/kit/hotkey-sheet"), "HotkeySheet");

function HotkeySheetHost() {
  // Read eagerly — it is a boolean in a store — so the chunk is fetched at the
  // moment somebody presses `?` and not before.
  const open = useHotkeySheetOpen();
  if (!open) return null;
  return (
    <Suspense>
      <LazyHotkeySheet />
    </Suspense>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFound,
  errorComponent: RouteError,
});

function RootLayout() {
  // THE TWO KEYBOARD LISTENERS, INSTALLED ONCE AND ONLY HERE. A screen that adds
  // its own is a second listener for the same keystroke: one on `window` beside
  // one on `document` fires the letter twice. (⌘K has its own, installed with
  // the palette behind sign-in, `lib/search.ts`: the `?` sheet's ⌘K row is
  // passive for exactly that reason.)
  useScreenHotkeyDispatcher();
  usePrimaryActionHotkey();

  return (
    <>
      <Outlet />
      <HotkeySheetHost />
      <Suspense>
        <Devtools />
      </Suspense>
    </>
  );
}
